import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { ImagePlus, Loader2, X as XIcon } from 'lucide-react';
import { fileToUploadB64, resolveUrl } from './driver/driverUtils';

export const uploadJobPhotos = async (authFetch, jobId, files, kind = 'job') => {
  let ok = 0;
  for (const f of files) {
    try {
      await authFetch('post', `/jobs/${jobId}/proof`, { kind, photos: [await fileToUploadB64(f)] });
      ok += 1;
    } catch (err) { toast.error(err?.response?.data?.detail || `Couldn't upload ${f.name}`); }
  }
  return ok;
};

const Thumb = ({ src, onRemove, testid }) => (
  <div className="relative shrink-0" data-testid={testid}>
    <a href={src} target="_blank" rel="noreferrer"><img src={src} alt="Job" className="w-20 h-20 object-cover rounded-md border border-slate-200" /></a>
    {onRemove && (
      <button type="button" onClick={onRemove} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-red-600 text-white inline-flex items-center justify-center shadow" aria-label="Remove photo" data-testid={`${testid}-remove`}><XIcon className="w-3.5 h-3.5" /></button>
    )}
  </div>
);

export const JobPhotos = ({ job, authFetch, onUpdated, canEdit = false, showCustomer = false, kind = 'job', title = 'Job photos' }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const photos = (job.proof_photos || []).filter(p => p.kind === kind);
  const custPhotos = showCustomer ? (job.photos || []) : [];
  const tid = kind === 'job' ? 'job-photos' : `${kind}-photos`;
  if (!canEdit && !photos.length) return null;
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 8);
    if (!files.length) return;
    setBusy(true);
    const ok = await uploadJobPhotos(authFetch, job.id, files, kind);
    if (ok) toast.success(`${ok} ${kind === 'waste' ? 'waste' : 'job'} photo${ok > 1 ? 's' : ''} added.`);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
    onUpdated?.();
  };
  const remove = async (url, k = kind) => {
    if (!window.confirm('Remove this photo?')) return;
    try { await authFetch('delete', `/admin/estimate/${job.id}/job-photo?kind=${k}&url=${encodeURIComponent(url)}`); toast.success('Photo removed.'); onUpdated?.(); }
    catch { toast.error("Couldn't remove photo."); }
  };
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid={`${tid}-${job.ref}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 text-[10px] font-black uppercase tracking-wider text-slate-500">{title} ({photos.length})</div>
        {canEdit && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="h-9 px-3 rounded-md border border-slate-200 bg-white text-xs font-bold text-[color:var(--mv-navy)] inline-flex items-center gap-1.5" data-testid={`${tid}-add-${job.ref}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4 text-[color:var(--mv-orange)]" />} Add photos
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={onFiles} data-testid={`${tid}-input-${job.ref}`} />
      </div>
      {photos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 pt-1.5">
          {photos.map((p, i) => <Thumb key={p.url} src={resolveUrl(p.url)} testid={`${kind}-photo-${job.ref}-${i}`} onRemove={canEdit ? () => remove(p.url) : null} />)}
        </div>
      )}
      {custPhotos.length > 0 && (
        <>
          <div className="text-[10px] font-black uppercase tracking-wider text-slate-500 pt-1">Customer's photos ({custPhotos.length})</div>
          <div className="flex gap-2 overflow-x-auto pb-1 pt-1.5" data-testid={`customer-photos-${job.ref}`}>
            {custPhotos.map((u, i) => <Thumb key={u} src={resolveUrl(u)} testid={`customer-photo-${job.ref}-${i}`} onRemove={canEdit ? () => remove(u, 'lead') : null} />)}
          </div>
        </>
      )}
    </div>
  );
};
